import styled, { keyframes } from "styled-components";
import { MessageSenderType as Sender } from "../../Common/Types-DTOs/Message";

export const ChatWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  align-items: center;
  justify-content: flex-start;
  background-color: transparent;
  gap: 0;
  position: relative;
  overflow: visible;
`;

export const ChatBody = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  overflow-y: auto;
  overflow-x: visible;
  scroll-behavior: smooth;
  flex-direction: column;
  flex-grow: 1;
  gap: 1.75rem;
  padding: 24px 48px;
  max-width: 1200px;
  margin: 10px auto;

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (max-width: ${(props) => props.theme.breakpoints.xl}) {
    padding: 16px 8px;
    margin: 10px;
    max-width: 100%;
  }
`;

export const ChatItemWrapper = styled.div<{
  $sender: Sender;
}>`
  width: 100%;
  height: auto;
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  position: relative;
  ${(props) =>
    props.$sender == Sender.USER
      ? `justify-content: flex-end;`
      : ``}
`;

export const ChatItemIcon = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  overflow: hidden;
  > svg {
    height: 1.25rem;
    width: 1.25rem;
    color: ${(props) => props.theme.colors.font.secondary};
  }
  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const ChatItemContent = styled.div<{
  $sender: Sender;
}>`
  display: flex;
  flex-direction: column;
  background-color: transparent;
  align-items: flex-start;
  ${(props) =>
    props.$sender == Sender.USER
      ? `
    flex: 0 0 auto;
    max-width: 75%;
    align-items: flex-end;
  `
      : `
    flex: 1;
    min-width: 0;
  `}
`;

export const ChatItemHeader = styled.div<{
  $sender: Sender;
}>`
  width: auto;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex: 0 0 auto;
  background-color: transparent;
  gap: ${(props) => props.theme.spacing.small};
  margin-bottom: 0.5rem;
  ${(props) =>
    props.$sender == Sender.USER &&
    `
    flex-direction: row-reverse;
  `}
`;

export const ChatItemSender = styled.span`
  font-size: ${(props) => props.theme.fontSizes.large};
  font-weight: 500;
  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    font-size: ${(props) => props.theme.fontSizes.medium};
  }
`;

export const ChatItemDate = styled.span<{ $sender: Sender }>`
  position: absolute;
  top: 0.25rem;
  ${(props) => props.$sender === Sender.USER ? `left: 0;` : `right: 0;`}
  font-size: ${(props) => props.theme.fontSizes.xsmall};
  font-weight: 300;
  color: ${(props) => props.theme.colors.font.grey2};
  opacity: 0;
  transition: opacity 0.15s ease;
  white-space: nowrap;
  pointer-events: none;

  ${ChatItemWrapper}:hover & {
    opacity: 1;
  }
`;

export const ChatItemMessage = styled.div<{
  $sender: Sender;
}>`
  word-break: break-word;
  white-space: pre-line;
  font-size: ${(props) => props.theme.fontSizes.medium};
  line-height: 1.6;
  box-sizing: border-box;
  ${(props) =>
    props.$sender === Sender.USER
      ? `
    width: auto;
    background-color: #1a1a1a;
    color: #ffffff;
    padding: ${props.theme.spacing.small} ${props.theme.spacing.medium};
    border-radius: 12px;
  `
      : `
    width: 100%;
    background-color: transparent;
    color: ${props.theme.colors.font.primary};
    padding: 0;
  `}
`;

export const ChatItemActions = styled.div<{
  $sender: Sender;
}>`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.25rem;
`;

export const ChatItemActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem;
  background-color: transparent;
  border: none;
  border-radius: 6px;
  color: #b0b7c3;
  cursor: pointer;
  transition: color 0.15s ease, transform 0.1s ease;

  &:hover {
    color: ${(props) => props.theme.colors.font.primary};
  }

  &:active {
    transform: scale(0.85);
  }

  svg {
    flex-shrink: 0;
  }
`;

export const ChatInputWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  bottom: 0;
  position: sticky;
  bottom: 0;
  z-index: 10;
  padding: 0 48px;
  box-sizing: border-box;
  gap: 0.5rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: ${(props) => props.theme.breakpoints.xl}) {
    padding: 0;
    max-width: 100%;
  }
`;

export const ChatInputContainer = styled.div`
  width: 100%;
  min-height: 3.5rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: ${(props) => props.theme.box.borderRadius || "12px"};
  background-color: #ffffff;
  padding: 0.75rem 1rem;
  box-sizing: border-box;
  gap: 0.5rem;

  &:focus-within {
    border-color: rgba(0, 0, 0, 0.3);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &:has(textarea:disabled) {
    background-color: #f5f5f5;
    border-color: rgba(0, 0, 0, 0.08);
    cursor: not-allowed;
  }

  > button {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 0 !important;
    min-width: 2.5rem;
    min-height: 2.5rem;
    width: 2.5rem;
    height: 2.5rem;
    gap: 0 !important;
    flex-shrink: 0;
    border-radius: 8px;

    > svg {
      color: #ffffff !important;
      margin: 0 !important;
      display: block;
      margin-right: 0 !important;
      scale: 1 !important;
    }
  }
`;

export const ChatInputDisclaimer = styled.p`
  font-size: ${(props) => props.theme.fontSizes.xsmall || "0.75rem"};
  color: ${(props) => props.theme.colors.font.grey2 || "#999999"};
  margin: 0;
  margin-top: 0.25rem;
  margin-bottom: 1rem;
  text-align: center;
  line-height: 1.4;
  width: 100%;
`;

export const StyledInput = styled.textarea`
  flex: 1;
  min-height: 2rem;
  max-height: 8rem;
  background-color: transparent;
  border: none;
  color: ${(props) => props.theme.colors.font.primary};
  padding: 0;
  box-sizing: border-box;
  font-size: ${(props) => props.theme.fontSizes.medium || "0.9375rem"};
  resize: none;
  line-height: 1.5;
  overflow-y: auto;
  font-family: inherit;
  &:focus {
    outline: none;
  }
  &::placeholder {
    color: ${(props) => props.theme.colors.font.grey2 || "#999999"};
  }

  &:disabled {
    cursor: not-allowed;
    color: #b0b7c3;
    background-color: transparent;
    opacity: 0.6;
  }
`;


const mercuryTypingAnimation = keyframes`
  0% {
    transform: translateY(0px);
    background-color: #44d0ca;
  }
  28% {
    transform: translateY(-7px);
    background-color: #c7f1ef;
  }
  44% {
    transform: translateY(0px);
    background-color: #44d0ca;
  }
`;

export const Dot = styled.span`
  animation: ${mercuryTypingAnimation} 1.8s infinite ease-in-out;
  background-color: #000;
  border-radius: 50%;
  height: 7px;
  vertical-align: middle;
  width: 7px;
  display: inline-block;
  margin-right: 0.3rem;

  &:nth-child(1) {
    animation-delay: 200ms;
  }

  &:nth-child(2) {
    animation-delay: 300ms;
  }

  &:nth-child(3) {
    animation-delay: 400ms;
  }

  &:last-child {
    margin-right: 0;
  }
`;

export const DocumentMessageWrapper = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${(props) => props.theme.spacing.small};
  flex: 0 0 auto;
`;

export const DocumentMessageIconWrapper = styled.div`
  width: 1.4rem;
  height: 1.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 0.2rem;
  background-color: ${(props) => props.theme.colors.accent1};

  > svg {
    height: 80%;
    width: 80%;
    color: ${(props) => props.theme.colors.primary};
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    width: 1.2rem;
    height: 1.2rem;
  }
`;

export const DocumentMessageName = styled.span`
  font-size: ${(props) => props.theme.fontSizes.small};
  font-weight: 600;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    font-size: 0.7rem;
  }
`;

export const DocumentDownloadButton = styled.div`
  width: 1.2rem;
  height: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 0.2rem;
  margin-left: ${(props) => props.theme.spacing.medium};

  > svg {
    height: 100%;
    width: 100%;
    color: ${(props) => props.theme.colors.accent1};
    cursor: pointer;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    width: 1rem;
    height: 1rem;
  }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`

export const StreamingCursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 1em;
  background-color: currentColor;
  vertical-align: middle;
  margin-left: 1px;
  animation: ${blink} 1s step-end infinite;
`

export const ScrollToBottomButton = styled.button`
  position: fixed;
  bottom: 9.5rem;
  right: 2rem;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #374151;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  font-size: 1.1rem;
  z-index: 50;
  animation: ${blink} 2s ease-in-out infinite;
  transition: background 0.15s, box-shadow 0.15s;

  &:hover {
    background: #f3f4f6;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  }
`

export const DateSeparator = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 0;
  margin: 0;
`;

export const DateSeparatorText = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  color: #6B7280;
  padding: 0.375rem 1rem;
  background-color: #F3F4F6;
  border-radius: 1rem;
  text-transform: capitalize;
`;
